import { Close, FilterList } from "@mui/icons-material";
import { Badge, Box, Button, useTheme } from "@mui/material";

export type FilterBarItem = {
  id: string;
  label: string;
  activeCount?: number;
};

export type FilterBarProps = {
  items: FilterBarItem[];
  clearAllLabel: string;
  moreFiltersLabel: string;
  showMoreFilters: boolean;
  hasActiveFilters: boolean;
  activeFilterId?: string;
  handleFilterClick: (filterId: string, anchor: HTMLElement) => void;
  handleMoreFiltersClick: (anchor: HTMLElement) => void;
  handleClearAll?: () => void;
};

export const FilterBar = ({
  items,
  clearAllLabel,
  moreFiltersLabel,
  showMoreFilters,
  hasActiveFilters,
  activeFilterId,
  handleFilterClick,
  handleMoreFiltersClick,
  handleClearAll,
}: FilterBarProps) => {
  const theme = useTheme();
  const controlHeight = theme.spacing(5);

  const buttonSx = (isActive: boolean) => ({
    borderRadius: theme.shape.borderRadius,
    borderColor: isActive ? theme.palette.primary.main : theme.palette.divider,
    color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
    backgroundColor: isActive
      ? theme.palette.action.selected
      : theme.palette.background.paper,
    textTransform: "none",
    height: controlHeight,
    minHeight: controlHeight,
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1,
      }}
    >
      {hasActiveFilters && (
        <Button
          variant="text"
          size="small"
          startIcon={<Close fontSize="small" />}
          style={{ opacity: activeFilterId ? 0.4 : 1 }}
          sx={{
            color: theme.palette.text.secondary,
            textTransform: "none",
            height: controlHeight,
            minHeight: controlHeight,
            whiteSpace: "nowrap",
          }}
          onClick={handleClearAll}
        >
          {clearAllLabel}
        </Button>
      )}
      {items.map((item) => {
        const activeCount = item.activeCount ?? 0;
        const isActive = activeCount > 0;
        const isSelected = activeFilterId === item.id;
        const button = (
          <Button
            variant="outlined"
            size="small"
            aria-label={isActive ? `${item.label} ${activeCount}` : item.label}
            style={{ opacity: activeFilterId && !isSelected ? 0.4 : 1 }}
            sx={{
              ...buttonSx(isActive),
            }}
            onClick={(event) => handleFilterClick(item.id, event.currentTarget)}
          >
            {item.label}
          </Button>
        );

        // Always a Badge: swapping the wrapper remounts the button, which detaches the open popper's anchor.
        return (
          <Badge
            key={item.id}
            badgeContent={activeCount}
            invisible={!isActive}
            color="primary"
            data-testid={
              isActive ? `filter-bar-item-${item.id}-count` : undefined
            }
            style={
              isActive
                ? { opacity: activeFilterId && !isSelected ? 0.4 : 1 }
                : undefined
            }
            sx={{
              "& .MuiBadge-badge": {
                top: 0,
                right: 0,
                transform: "translate(50%, -50%)",
                minWidth: theme.spacing(2.25),
                height: theme.spacing(2.25),
                color: theme.palette.common.white,
                fontSize: theme.typography.caption.fontSize,
              },
            }}
          >
            {button}
          </Badge>
        );
      })}
      {showMoreFilters && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterList />}
          style={{ opacity: activeFilterId ? 0.4 : 1 }}
          sx={buttonSx(false)}
          onClick={(event) => handleMoreFiltersClick(event.currentTarget)}
        >
          {moreFiltersLabel}
        </Button>
      )}
    </Box>
  );
};
